// AppShell top bar (Spec §6.1) — Workspace Switcher, the focus rail, then the
// right-hand icons: Search, Help (opens Resources panel), Notification bell and
// avatar menu.
// In Org/Aggregator workspaces it shows the workspace name + colour chip so the
// user always knows where they are.
//
// Story #5648. THE FIGURES DO NOT ALL DRAW THE SAME TOP BAR, and rounds 2-4
// were spent discovering that. Read from the figure HTML itself (round 4), not
// from anybody's summary of it:
//
//   PERSONAL      C2, C5, S1, S2, S3, S4, S4b — a search TEXT BOX, FIRST,
//                 before the workspace chip. Placeholder "Search…" on six of
//                 the seven; C2 alone reads "Search projects, patents, people…"
//                 (its box is 240px where theirs are ~170px).
//   ORGANISATION  C4 — a search TEXT BOX, AFTER the chip, "Search members,
//                 projects…".
//   AGGREGATOR    C1, S5, S6, S7 — NO box at all: a magnifier ICON on the far
//                 right, `<div class="tb-ic" title="Search">🔎</div>`, beside
//                 help, the bell and the avatar.
//
// So the bar renders BY WORKSPACE. Everything else is constant: the rail (only
// where a page declares a spine spec), then a spacer, then help · bell ·
// avatar.

import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Menu, Search, HelpCircle, ChevronDown, LogOut, User as UserIcon, X } from 'lucide-react'
import NotificationBell from '../notifications/NotificationBell'
import WorkspaceSwitcher from './WorkspaceSwitcher'
import { TB_IC } from './topBarIcon'
import { FocusRail, useSpine } from '../spine'
import { useAuth } from '../../contexts/AuthContext'
import { useWorkspace } from '../../contexts/WorkspaceContext'

interface TopBarProps {
  onOpenMobileNav: () => void
  onOpenHelp: () => void
}

/**
 * What the search box says it will search, per the figure that draws it.
 *
 * Placeholders are per-FIGURE, not per-workspace: the personal figures mostly
 * say "Search…" because their box is ~170px wide, and C2's says more because
 * its box is 240px. Both are kept rather than averaged, because a placeholder
 * naming what is searchable from where you stand is the better UI and it is
 * what was approved. Every value below was read out of the figure HTML.
 */
const SEARCH_PLACEHOLDER: Record<string, string> = {
  // screen-C4-org-policy.html
  organisation: 'Search members, projects…',
  // screen-C5/S1/S2/S3/S4/S4b — the personal default
  personal: 'Search…',
  admin: 'Search…',
}

/**
 * One route-specific placeholder, because one figure draws one.
 *
 * ROUND 7. The figure's own words — "Search projects, patents, people…" — do
 * not fit a real 240px input on one line. Measured on the live bar: the string
 * needs about 214px at 12.5px and the input's text area is 176px, so it was
 * rendering as "Search projects, patents, pe" with the tail sliced off
 * mid-word and 25px of the box still empty. The figure only fits it because it
 * draws a <div>, which wraps onto a second line inside a 58px bar; an <input>
 * placeholder cannot wrap.
 *
 * No 240px box holds that sentence, so the sentence is the thing that gives:
 * "Search projects, patents…" is about 162px and fits with room to spare, and
 * it still names what is searchable from where you stand — which is the whole
 * point of C2 carrying its own placeholder at all. The ellipsis carries the
 * "…and more" the third noun used to.
 */
const SEARCH_PLACEHOLDER_BY_ROUTE: Record<string, string> = {
  // screen-C2-who-can-see.html
  '/account/sharing': 'Search projects, patents…',
}

/**
 * How WIDE the box is, per the figure that draws it — measured out of the
 * figure HTML, not eyeballed:
 *
 *   S1, S2, S3          .tb-search{flex:0 0 168px}   ← the three under review
 *   S4, S4b, C5         .tb-search{flex:0 0 172px}
 *   C2                  .tb-search{flex:0 0 240px}
 *   C1, S5, S6, S7      no box at all — a magnifier among the right-hand icons
 *   C4                  .tb-search style="max-width:360px" — FLEXIBLE, capped
 *
 * Live was 224px (`lg:w-56`) on every one of them, a third over the inventor
 * figures, and it is the last thing holding S1/S2/S3 at PARTIAL.
 *
 * So the width is per family, NOT global. Hard-coding 168px into this shared
 * component would have broken five figures to fix three. 168 and 172 are one
 * target at this tolerance; C2's 240 is not, and it is not a stylistic
 * difference either: C2 is the one figure whose placeholder is longer, and a
 * box says what it can hold.
 *
 * ROUND 7 — C4 is the ONE figure that draws the flexible box, and it caps it:
 * `<div class="tb-search" style="max-width:360px">`. Every other figure either
 * pins a fixed width or draws no box. Nothing in the set uses kit.css's
 * uncapped 460px default, so the organisation family gets `flex-1` capped at
 * C4's own 360px — not the 288px (`lg:w-72`) fixed box that stood here, which
 * was neither flexible nor 360.
 */
const SEARCH_WIDTH: Record<string, string> = {
  // S1/S2/S3/S4/S4b/C5 — one narrow box, no responsive step-up: the figures
  // draw a fixed width and the rail needs the room beside it.
  personal: 'w-[168px] shrink-0',
  admin: 'w-[168px] shrink-0',
  // screen-C4-org-policy.html — flexible, capped at 360px, exactly as drawn.
  organisation: 'min-w-0 flex-1 max-w-[360px]',
}

/** The one route whose figure draws a wider fixed box. */
const SEARCH_WIDTH_BY_ROUTE: Record<string, string> = {
  // screen-C2-who-can-see.html — .tb-search{flex:0 0 240px}
  '/account/sharing': 'w-[240px] shrink-0',
}

/**
 * And how BIG its text is. The inventor figures say `font-size:12.5px`
 * explicitly; kit.css's default is 13px. Live was 14px (`text-sm`).
 *
 * This is not decoration — it is what makes the words fit. C2's box is 240px
 * with 48px of padding, and even at 12.5px the figure's three-noun placeholder
 * overran it (see SEARCH_PLACEHOLDER_BY_ROUTE). Type size buys room; it did not
 * buy enough, so the words came down too.
 */
export function searchFontClass(kind: string): string {
  return kind === 'personal' || kind === 'admin' ? 'text-[12.5px]' : 'text-sm'
}

/**
 * A placeholder that outgrows its box must DEGRADE, not be sliced mid-word.
 *
 * `truncate` is overflow:hidden + text-overflow:ellipsis + white-space:nowrap,
 * and browsers apply text-overflow to an input's placeholder as well as to its
 * value. Round 7 shortened the one placeholder that overran today; this is what
 * stops the next long one from rendering as "Search projects, patents, pe".
 */
export const SEARCH_INPUT_OVERFLOW = 'truncate'

export function searchWidthClass(kind: string, pathname: string): string {
  if (kind === 'personal' || kind === 'admin') {
    const byRoute = SEARCH_WIDTH_BY_ROUTE[pathname]
    if (byRoute) return byRoute
  }
  return SEARCH_WIDTH[kind] ?? 'w-[168px]'
}

function searchPlaceholder(kind: string, pathname: string): string {
  if (kind === 'personal' || kind === 'admin') {
    const byRoute = SEARCH_PLACEHOLDER_BY_ROUTE[pathname]
    if (byRoute) return byRoute
  }
  return SEARCH_PLACEHOLDER[kind] ?? 'Search…'
}

export default function TopBar({ onOpenMobileNav, onOpenHelp }: TopBarProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { active } = useWorkspace()
  // A graduated screen carries the rail, which needs the width — so the avatar
  // drops its chevron there, in the same order the figures show.
  const graduated = useSpine() !== null
  // Only the aggregator figures draw the magnifier. Everywhere else the box is
  // open on arrival, as drawn.
  const searchIsIcon = active.kind === 'aggregator'
  const [q, setQ] = useState('')
  const [searchOpen, setSearchOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)
  const phoneSearchRef = useRef<HTMLInputElement>(null)
  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  // Opening the icon has to put the caret in the field, or the click costs a
  // second one.
  // Only one of the two fields is ever displayed (the phone overlay below 640px,
  // the inline one from sm up); focus() on the hidden one is a no-op.
  useEffect(() => {
    if (searchOpen) {
      phoneSearchRef.current?.focus()
      searchRef.current?.focus()
    }
  }, [searchOpen])

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  function onSearch(e: FormEvent) {
    e.preventDefault()
    const term = q.trim()
    if (term) navigate(`/search?q=${encodeURIComponent(term)}`)
  }

  const initials = (user?.name || user?.email || '?')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('')

  const placeholder = searchPlaceholder(active.kind, pathname)

  // The box, as the personal and organisation figures draw it: open on arrival,
  // its placeholder naming what is searchable from where you stand. It narrows
  // on a graduated screen because the rail needs the room, exactly as the
  // figures do (C2's box is 240px, C5's 172px).
  const searchBox = (
    <form
      onSubmit={onSearch}
      className={`hidden sm:block ${searchWidthClass(active.kind, pathname)}`}
      role="search"
      data-part="TopBarSearch"
    >
      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          type="search"
          placeholder={placeholder}
          aria-label="Global search"
          className={`w-full ${SEARCH_INPUT_OVERFLOW} rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 ${searchFontClass(active.kind)} text-slate-800 placeholder:text-slate-400 focus:border-iflow-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-iflow-100`}
        />
      </div>
    </form>
  )

  // The magnifier, as the aggregator figures draw it — an icon among the
  // right-hand icons. It still opens the real field, so the capability is the
  // same one; only the resting state differs.
  const searchIcon = searchOpen ? (
    <form onSubmit={onSearch} className="hidden w-44 shrink-0 sm:block lg:w-56" role="search" data-part="TopBarSearch">
      <div className="relative">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
        <input
          ref={searchRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onBlur={() => { if (!q.trim()) setSearchOpen(false) }}
          onKeyDown={(e) => { if (e.key === 'Escape') { setQ(''); setSearchOpen(false) } }}
          type="search"
          placeholder={placeholder}
          aria-label="Global search"
          className={`w-full ${SEARCH_INPUT_OVERFLOW} rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-iflow-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-iflow-100`}
        />
      </div>
    </form>
  ) : (
    <button
      type="button"
      onClick={() => setSearchOpen(true)}
      // C1/S5/S6/S7 draw it in the same 34×34 box as help and the bell:
      // `<div class="tb-ic" title="Search">🔎</div>`.
      className={`${TB_IC} text-slate-600 hover:bg-slate-50 hover:text-iflow-700`}
      aria-label="Search"
      title={placeholder}
    >
      <Search size={18} aria-hidden />
    </button>
  )

  // UX-013 / M7: below 640px the text box is hidden for every workspace, so
  // every workspace gets the magnifier there (the aggregator icon above already
  // covers that workspace at every width). Opening it takes the whole bar —
  // the field cannot share a 390px bar with the chip, help, the bell and the
  // avatar without pushing the avatar menu (Sign out) off screen again.
  const phoneSearchIcon = searchIsIcon ? null : (
    <button
      type="button"
      onClick={() => setSearchOpen(true)}
      className={`${TB_IC} text-slate-600 hover:bg-slate-50 hover:text-iflow-700 sm:hidden`}
      aria-label="Search"
      title={placeholder}
      data-part="TopBarSearchIcon"
    >
      <Search size={18} aria-hidden />
    </button>
  )

  const phoneSearchOverlay = searchOpen ? (
    <form
      onSubmit={(e) => { onSearch(e); setSearchOpen(false) }}
      className="absolute inset-0 z-30 flex items-center gap-2 bg-white px-4 sm:hidden"
      role="search"
      data-part="TopBarSearchPhone"
    >
      <div className="relative min-w-0 flex-1">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
        <input
          ref={phoneSearchRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') { setQ(''); setSearchOpen(false) } }}
          type="search"
          placeholder={placeholder}
          aria-label="Global search"
          className={`w-full ${SEARCH_INPUT_OVERFLOW} rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-iflow-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-iflow-100`}
        />
      </div>
      <button
        type="button"
        onClick={() => { setQ(''); setSearchOpen(false) }}
        className={`${TB_IC} text-slate-600 hover:bg-slate-50`}
        aria-label="Close search"
      >
        <X size={18} aria-hidden />
      </button>
    </form>
  ) : null

  return (
    /* ROUND 7 — the bar's own scale, charged by every review since round 1.
       kit.css: `.topbar{height:58px;flex:0 0 58px}`, and NOT ONE of the twelve
       figures overrides it (S1/S2/S3 override only its gap and padding). Live
       was h-16 = 64px — 6px taller than every figure, on every screen. */
    <header className="sticky top-0 z-20 flex h-[58px] items-center gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur">
      <button
        type="button"
        onClick={onOpenMobileNav}
        className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 lg:hidden"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>

      {/* Order, per figure. PERSONAL (C2, C5, S1-S4b): search box, then the
          chip, then the rail. ORGANISATION (C4): chip, then the search box —
          which is the ordering the C4 round-2 review asked for and confirmed.
          AGGREGATOR (C1, S5-S7): chip, then the rail, and the magnifier sits
          with the right-hand icons below. */}
      {!searchIsIcon && active.kind !== 'organisation' ? searchBox : null}
      <WorkspaceSwitcher />
      {!searchIsIcon && active.kind === 'organisation' ? searchBox : null}
      {graduated ? <FocusRail /> : null}

      {/* shrink-0: the icons and the avatar menu are never the thing that
          gives when the bar is short of room (UX-013). */}
      <div className="ml-auto flex shrink-0 items-center gap-1">
        {searchIsIcon ? searchIcon : null}
        {phoneSearchIcon}
        <button
          type="button"
          onClick={onOpenHelp}
          /* ROUND 7 — every figure draws help and the bell as
             `<div class="tb-ic">`: a 34×34 white box with a 1px border and a
             9px radius (kit.css `.tb-ic`, no figure overrides it). Live drew
             both as bare glyphs on the bar. The word "Help" goes with the
             change — a 34px box cannot hold it, and no figure in the set draws
             a labelled help control; the label lives in the accessible name
             and the tooltip, where it is read out rather than squinted at. */
          className={`${TB_IC} text-slate-600 hover:bg-slate-50 hover:text-iflow-700`}
          aria-label="Open help & resources"
          title="Help & resources"
        >
          <HelpCircle size={19} />
        </button>

        <NotificationBell />

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            className="flex items-center gap-2 rounded-lg p-1 pr-2 hover:bg-slate-100"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-iflow-600 text-sm font-semibold text-white">
              {initials || <UserIcon size={16} />}
            </span>
            {!graduated && <ChevronDown size={15} className="hidden text-slate-400 sm:block" aria-hidden />}
          </button>
          {menuOpen && (
            <div role="menu" className="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl">
              <div className="border-b border-slate-100 px-3 py-2">
                <p className="truncate text-sm font-medium text-iflow-900">{user?.name || 'Account'}</p>
                <p className="truncate text-xs text-slate-400">{user?.email}</p>
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={() => { setMenuOpen(false); navigate('/settings') }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
              >
                <UserIcon size={16} aria-hidden /> Account settings
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => { setMenuOpen(false); logout({ navigate: () => navigate('/home') }) }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-rose-600 hover:bg-rose-50"
              >
                <LogOut size={16} aria-hidden /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
      {phoneSearchOverlay}
    </header>
  )
}
